const getFileExtensions = (filename) => {
  const exts = filename.split('.');
  if (exts.length < 4) return exts;
  return [exts.slice(0, exts.length - 2), exts[exts.length - 2], exts[exts.length - 1]];
};

// CompressionExtensions lists the built-in compression extensions that are supported
const COMPRESSION_EXTENSIONS = ['zip', 'gz', 'bz2', '7z'];

// MODEL_EXTENSIONS lists the extensions that represents a model
const MODEL_EXTENSIONS = ['mps', 'rew', 'dua', 'lp', 'rlp', 'ilp', 'dlp', 'opb'];

// INITIALIZATION_EXTENSIONS lists the extensions that can be used in addition to a model
const INITIALIZATION_EXTENSIONS = ['mst', 'hnt', 'ord', 'bas', 'sol', 'attr'];

// PARAMETER_EXTENSIONS lists the extensions of parameters
const PARAMETER_EXTENSIONS = ['prm', 'env'];

const ALL_INPUTFILE_EXTENSIONS = []
  .concat(...MODEL_EXTENSIONS.concat(INITIALIZATION_EXTENSIONS, PARAMETER_EXTENSIONS)
              .map(mext => [mext, ...COMPRESSION_EXTENSIONS.map(cext => `${mext}.${cext}`)]));

const MODEL_GROUP = 'model';
const SOLUTION_GROUP = 'solution';
const INITIALIZATION_GROUP = 'init';
const PARAMETER_GROUP = 'param';

const OUTPUT_FILE_TYPES = [
  {
    ext: 'json',
    group: SOLUTION_GROUP,
    title: 'Export the solution in a JSON format',
    description: 'Exports the solution consisting of tagged variables in a JSON document. This format requires tags in an ATTR file provided as input.',
  },
  {
    ext: 'sol',
    group: SOLUTION_GROUP,
    title: 'Export the solution vector',
    description: 'The file consists of variable-value pairs, each on its own line. The file contains one line for each variable in the model.',
  },
  {
    ext: 'mps',
    group: MODEL_GROUP,
    familly: 'mps',
    title: 'Export the model as an MPS file',
    description: 'The MPS file format is the oldest and most widely used format for storing math programming models.',
  },
  {
    ext: 'rew',
    group: MODEL_GROUP,
    familly: 'mps',
    title: 'Export the model as an REW file',
    description: 'The REW format is identical to the MPS format, except in how objects are named when files are written. When writing an REW format file, the Gurobi optimizer ignores the given names and instead refers to the variables using a set of default names that are based on row and column numbers.',
  },
  {
    ext: 'dua',
    group: MODEL_GROUP,
    familly: 'mps',
    title: 'Export the dual model in an MPS file format',
    description: 'The DUA file format is the MPS format representation of a dual model.',
  },
  {
    ext: 'lp',
    group: MODEL_GROUP,
    familly: 'lp',
    title: 'Export the model as an LP file',
    description: 'The LP format captures an optimization model in a way that is easier for humans to read than MPS format.',
  },
  {
    ext: 'rlp',
    group: MODEL_GROUP,
    familly: 'lp',
    title: 'Export the model as an RLP file',
    description: 'The RLP format is identical to the LP format, except in how objects are named when files are written. When writing an RLP format file, the Gurobi optimizer ignores the given names and instead refers to the variables using names that are based on variable or constraint characteristics.',
  },
  {
    ext: 'ilp',
    group: MODEL_GROUP,
    familly: 'lp',
    title: 'Export the IIS model in an LP format',
    description: 'The ILP file format is identical to the LP format. The only difference is in how they are used. ILP files are specifically used to write computed Irreducible Inconsistent Subsystem (IIS) models.',
  },
  {
    ext: 'dlp',
    group: MODEL_GROUP,
    familly: 'lp',
    title: 'Export the dual model in an LP format',
    description: 'The DLP file format is the LP format representation of a dual model.',
  },
  {
    ext: 'attr',
    group: INITIALIZATION_GROUP,
    title: 'Export model attributes',
    description: 'The ATTR file contains the metadata set in the model such as tags attached to variables.',
  },
  {
    ext: 'mst',
    group: INITIALIZATION_GROUP,
    title: 'Export MIP start data',
    description: 'When imported later on into a MIP model, it will allow the MIP model to start with a known feasible solution.',
  },
  {
    ext: 'bas',
    group: INITIALIZATION_GROUP,
    title: 'Export the simplex basis information',
    description: 'When imported later on into a continuous models, it will allow the simplex algorithm to start from the given simplex basis.',
  },
  {
    ext: 'prm',
    group: PARAMETER_GROUP,
    title: 'Export parameter values',
    description: 'The parameter file contains all the changed parameters that were set to solve the model.',
  },
];

const ValidateFileExtension = (filename) => {
  // http://github.compute.gurobi.com/Gurobi/grbrs-ng/blob/master/gowork/src/github.compute.gurobi.com/goclient/pkg/batchvalidator/validator.go
  const [, ext, cext] = getFileExtensions(filename);
  if (cext && (COMPRESSION_EXTENSIONS.indexOf(cext) < 0)) {
    return `Compression ${cext} not supported, use ${COMPRESSION_EXTENSIONS.join(', ')}`;
  }

  if ((MODEL_EXTENSIONS.indexOf(ext) < 0)
      && (INITIALIZATION_EXTENSIONS.indexOf(ext) < 0)
      && (PARAMETER_EXTENSIONS.indexOf(ext) < 0)) {
    return `Extension '${ext}' not supported, use ${MODEL_EXTENSIONS.join(', ')} for models, ${PARAMETER_EXTENSIONS.join(', ')} for parameters, and ${INITIALIZATION_EXTENSIONS.join(', ')} for other input files.`;
  }
  // This validation is not complete as the context of this method does not provide
  // the files dropped or selected by the user.
  // Also, validateFile is invoked once, when the file is selected
  // As the file list can change after the file is selected, validation involving
  // the complete set of files must be performed each time the file list changes.
  // See validateNode for validation that involves all the files.
  return null;
};

const StartValidateFileSet = (params) => {
  const ctx = params || {};
  ctx.exts = {};
  return ctx;
};

const ValidateFileInSet = (filename, ctx) => {
  const [, ext] = getFileExtensions(filename);
  ctx.exts[ext] = (ctx.exts[ext] || 0) + 1;
  if (MODEL_EXTENSIONS.indexOf(ext) >= 0) {
    if (ctx.model) {
      return `Cannot specify multiple models, use a single file with extension ${MODEL_EXTENSIONS.join(', ')}`;
    }
    ctx.model = true;
  } else if (INITIALIZATION_EXTENSIONS.indexOf(ext) >= 0) {
    if (ctx.input) {
      return `Cannot specify multiple files with extension ${INITIALIZATION_EXTENSIONS.join(', ')}`;
    }
    ctx.input = true;
  }
  return null;
};

const StopValidateFileSet = () => {};

const GetValidFileExtensions = () => {
  return [COMPRESSION_EXTENSIONS, MODEL_EXTENSIONS, INITIALIZATION_EXTENSIONS, PARAMETER_EXTENSIONS];
};

export {
  getFileExtensions,
  ValidateFileExtension,
  StartValidateFileSet,
  ValidateFileInSet,
  StopValidateFileSet,
  GetValidFileExtensions,
  MODEL_EXTENSIONS,
  INITIALIZATION_EXTENSIONS,
  PARAMETER_EXTENSIONS,
  COMPRESSION_EXTENSIONS,
  ALL_INPUTFILE_EXTENSIONS,
  OUTPUT_FILE_TYPES,
  MODEL_GROUP,
  INITIALIZATION_GROUP,
  PARAMETER_GROUP,
  SOLUTION_GROUP,
};
